// =====================================================================
// EmittedDocuments (PME Sprint 3b / S52 · modelos com campos S53) — documentos
// EMITIDOS do paciente: receita / atestado / pedido de exame / TCLE, em PDF.
// 🔒 LGPD: PDF servido auth-gated (blob com token) + multi-tenant + audit.
//
// S53: MODELOS PRONTOS com CAMPOS estruturados (uso comum). O médico escolhe o
// modelo, preenche campos limpos (dias, data já vem HOJE, hora já vem AGORA,
// medicamento) e o conteúdo se MONTA sozinho — sem "___" pra apagar. Sempre dá
// pra deixar "Em branco" e escrever do zero, e o texto montado é 100% editável.
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from "@material-ui/core";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import DescriptionIcon from "@material-ui/icons/Description";
import VisibilityIcon from "@material-ui/icons/Visibility";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";
import SendIcon from "@material-ui/icons/Send";
import { toast } from "react-toastify";

import { listEncounterProfessionals } from "../../services/encounterService";
import {
  DOC_TYPE_LABELS,
  emitDocument,
  listEmittedDocuments,
  getEmittedDocObjectUrl,
  deleteEmittedDoc,
  emitDocErrorMessage
} from "../../services/fileService";
import ReceituarioPicker from "./ReceituarioPicker";
import SendDocumentDialog from "./SendDocumentDialog";
import useIsMobile from "../../utils/useIsMobile";

const DOC_TYPE_OPTIONS = [
  { value: "prescription", label: "Receita médica" },
  { value: "medical_certificate", label: "Atestado médico" },
  { value: "exam_request", label: "Pedido de exame" },
  { value: "referral", label: "Encaminhamento" },
  { value: "tcle", label: "Termo de consentimento (TCLE)" }
];

const BODY_PLACEHOLDER = {
  prescription: "Ex: Losartana 50mg — 1 comprimido pela manhã, uso contínuo.",
  medical_certificate:
    "Ex: Atesto que o(a) paciente necessita de afastamento de suas atividades por 2 dias a partir desta data, por motivo de saúde.",
  exam_request: "Ex: Solicito: Hemograma completo, Glicemia de jejum, TSH.",
  referral:
    "Ex: Encaminho o(a) paciente ao cardiologista para avaliação de hipertensão de difícil controle.",
  tcle:
    "Ex: Declaro que fui informado(a) de forma clara sobre o procedimento, seus objetivos, riscos e alternativas, e consinto livremente com sua realização."
};

// ---- datas/horas pra defaults dinâmicos dos campos ----
const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const nowHM = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const dBR = (iso) => {
  if (!iso) return "___/___/______";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

// Número por extenso (0-99) — pro atestado formal "7 (sete) dias" (anti-fraude).
const _UNI = ["zero", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "catorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
const _DEZ = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
const extenso = (n) => {
  const num = parseInt(n, 10);
  if (isNaN(num) || num < 0 || num > 99 || String(n).trim() === "") return null;
  if (num < 20) return _UNI[num];
  const d = Math.floor(num / 10);
  const u = num % 10;
  return u === 0 ? _DEZ[d] : `${_DEZ[d]} e ${_UNI[u]}`;
};

// ---------------------------------------------------------------------
// MODELOS por tipo. Cada um pode ter:
//   fields: [{ key, label, type, default, width }]  + build(values) -> texto
//   (sem fields -> body estático). default "@today"/"@now" resolvem na hora.
// ---------------------------------------------------------------------
const DOC_TEMPLATES = {
  prescription: [
    {
      id: "simples",
      label: "Receita simples",
      title: "",
      fields: [
        { key: "med", label: "Medicamento + dosagem", type: "text", width: 220 },
        { key: "pos", label: "Posologia", type: "text", width: 220 }
      ],
      build: (v) =>
        `1. ${v.med || "___ (medicamento)"} — ${v.pos || "___ (posologia)"}.\n\nOrientações: hidratação adequada e repouso. Retornar se houver piora dos sintomas.`
    },
    {
      id: "continuo",
      label: "Uso contínuo",
      title: "Uso contínuo",
      fields: [
        { key: "med", label: "Medicamento + dosagem", type: "text", width: 220 },
        { key: "pos", label: "Posologia", type: "text", width: 220 }
      ],
      build: (v) =>
        `Medicamento de uso contínuo:\n1. ${v.med || "___ (medicamento)"} — ${v.pos || "___ (posologia)"}.\n\nMantém uso contínuo conforme orientação.`
    }
  ],
  medical_certificate: [
    {
      id: "afastamento",
      label: "Afastamento",
      title: "Afastamento",
      fields: [
        { key: "dias", label: "Dias", type: "number", default: "1", width: 90 },
        { key: "inicio", label: "A partir de", type: "date", default: "@today", width: 160 },
        { key: "cid", label: "CID-10 (opcional)", type: "text", width: 150 }
      ],
      build: (v) => {
        const ext = extenso(v.dias);
        const diasTxt = v.dias ? (ext ? `${v.dias} (${ext})` : v.dias) : "___";
        return (
          `Atesto, para os devidos fins, que o(a) paciente esteve sob meus cuidados médicos nesta data, necessitando de afastamento de suas atividades por ${diasTxt} dia(s) a partir de ${dBR(v.inicio)}, por motivo de saúde.` +
          (v.cid ? `\n\nCID-10: ${v.cid} (informado mediante autorização do paciente).` : "")
        );
      }
    },
    {
      id: "comparecimento",
      label: "Comparecimento",
      title: "Comparecimento",
      fields: [
        { key: "data", label: "Data", type: "date", default: "@today", width: 160 },
        { key: "ini", label: "Das", type: "time", width: 110 },
        { key: "fim", label: "Às", type: "time", default: "@now", width: 110 }
      ],
      build: (v) =>
        `Atesto, para os devidos fins, que o(a) paciente compareceu a atendimento médico nesta clínica no dia ${dBR(
          v.data
        )}, no período das ${v.ini || "___"} às ${v.fim || "___"}.`
    },
    {
      id: "acompanhante",
      label: "Acompanhante",
      title: "Acompanhante",
      fields: [
        { key: "nome", label: "Nome do acompanhante", type: "text", width: 220 },
        { key: "data", label: "Data", type: "date", default: "@today", width: 160 },
        { key: "ini", label: "Das", type: "time", width: 110 },
        { key: "fim", label: "Às", type: "time", default: "@now", width: 110 }
      ],
      build: (v) =>
        `Atesto, para os devidos fins, que ${
          v.nome || "___"
        } acompanhou o(a) paciente em atendimento médico nesta clínica no dia ${dBR(
          v.data
        )}, no período das ${v.ini || "___"} às ${v.fim || "___"}.`
    },
    {
      id: "aptidao_fisica",
      label: "Aptidão física",
      title: "Atestado de aptidão física",
      fields: [
        { key: "atividade", label: "Atividade/modalidade (opcional)", type: "text", width: 240 },
        { key: "validade", label: "Validade (opcional)", type: "text", width: 160 }
      ],
      build: (v) =>
        `Atesto, para os devidos fins, que o(a) paciente foi submetido(a) a avaliação clínica nesta data e encontra-se APTO(A) para a prática de ${
          v.atividade || "atividades físicas"
        }, não tendo sido constatadas, ao exame clínico, contraindicações para a sua realização.` +
        (v.validade ? `\n\nValidade deste atestado: ${v.validade}.` : "")
    }
  ],
  exam_request: [
    {
      id: "solicitacao",
      label: "Solicitação de exames",
      title: "",
      fields: [
        { key: "exames", label: "Exames (um por linha)", type: "multiline", width: 460 },
        { key: "cid", label: "Hipótese diagnóstica / CID-10", type: "text", width: 300 }
      ],
      build: (v) => {
        const lista = (v.exames || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => `- ${s}`)
          .join("\n");
        return (
          `Solicito os seguintes exames para o(a) paciente:\n\n${lista || "- ___"}` +
          (v.cid ? `\n\nHipótese diagnóstica (CID-10): ${v.cid}.` : "")
        );
      }
    },
    {
      id: "rotina",
      label: "Laboratoriais de rotina (lista pronta)",
      title: "Exames de rotina",
      body:
        "Solicito:\n- Hemograma completo\n- Glicemia de jejum\n- Colesterol total e frações\n- Triglicerídeos\n- TSH e T4 livre\n- Ureia e creatinina\n- TGO / TGP\n- Vitamina D (25-OH)"
    }
  ],
  referral: [
    {
      id: "encaminhamento",
      label: "Encaminhamento",
      title: "Encaminhamento Médico",
      fields: [
        { key: "espec", label: "Encaminhar para (especialidade/profissional)", type: "text", width: 300 },
        { key: "motivo", label: "Motivo / hipótese diagnóstica", type: "text", width: 300 }
      ],
      build: (v) =>
        `Encaminho o(a) paciente para avaliação e conduta com ${
          v.espec || "___ (especialidade)"
        }.` +
        (v.motivo ? `\n\nMotivo / hipótese diagnóstica: ${v.motivo}.` : "") +
        `\n\nColoco-me à disposição para os esclarecimentos necessários. Atenciosamente,`
    }
  ],
  tcle: [
    {
      id: "generico",
      label: "TCLE genérico",
      title: "Termo de Consentimento Livre e Esclarecido",
      fields: [{ key: "proc", label: "Procedimento / tratamento", type: "text", width: 360 }],
      build: (v) =>
        `Declaro que fui informado(a) de forma clara e detalhada pelo(a) profissional responsável sobre o procedimento/tratamento proposto: ${
          v.proc || "___"
        }.\n\nFui esclarecido(a) sobre seus objetivos, benefícios esperados, riscos, possíveis complicações e alternativas existentes, tendo tido a oportunidade de tirar dúvidas, todas respondidas satisfatoriamente.\n\nConsinto, de forma livre e esclarecida, com a realização do procedimento acima descrito.`
    }
  ]
};

// Resolve os defaults dos campos (@today/@now) num objeto de valores iniciais.
const initValues = (tpl) => {
  const v = {};
  (tpl.fields || []).forEach((f) => {
    let d = f.default || "";
    if (d === "@today") d = todayISO();
    else if (d === "@now") d = nowHM();
    v[f.key] = d;
  });
  return v;
};

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  title: { display: "flex", alignItems: "center", gap: theme.spacing(1), fontWeight: 600 },
  card: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  info: { flex: 1, minWidth: 0 },
  docTitle: { fontWeight: 600, wordBreak: "break-word" },
  meta: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 2 },
  actions: { display: "flex", gap: theme.spacing(0.5), flexShrink: 0 },
  empty: { color: theme.palette.text.secondary, fontStyle: "italic", padding: theme.spacing(1, 0) },
  fieldsBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    margin: theme.spacing(0.5, 0, 0.5),
    borderRadius: 6,
    border: `1px dashed ${theme.palette.divider}`,
    background: theme.palette.action.hover
  }
}));

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const EmittedDocuments = ({ patientId, patient }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [professionals, setProfessionals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ documentType: "prescription", professionalId: "", title: "", body: "" });
  const [templateId, setTemplateId] = useState("");
  const [tplValues, setTplValues] = useState({}); // valores dos campos do modelo ativo
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null); // doc a enviar (abre SendDocumentDialog)

  // modelo ativo (objeto) e seus campos
  const activeTpl =
    templateId && (DOC_TEMPLATES[form.documentType] || []).find((t) => t.id === templateId);
  const activeFields = (activeTpl && activeTpl.fields) || null;

  // profissional selecionado (pro título do picker do receituário)
  const selectedProf = professionals.find(
    (p) => String(p.id) === String(form.professionalId)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listEmittedDocuments(patientId);
      setDocs((data && data.documents) || []);
    } catch (err) {
      toast.error(emitDocErrorMessage(err));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await listEncounterProfessionals();
        if (active) setProfessionals((r && r.professionals) || []);
      } catch (e) {
        /* dropdown best-effort */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const openDialog = () => {
    setForm({ documentType: "prescription", professionalId: "", title: "", body: "" });
    setTemplateId("");
    setTplValues({});
    setDialogOpen(true);
  };

  // S53: captura o value ANTES do updater (pooling de eventos do React).
  const setField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Troca de tipo: reseta o modelo (modelos são por tipo).
  const setType = (e) => {
    const value = e.target.value;
    setTemplateId("");
    setTplValues({});
    setForm((prev) => ({ ...prev, documentType: value }));
  };

  // Aplica um modelo. Com campos -> inicializa valores (datas/horas dinâmicas) e
  // monta o corpo; sem campos -> usa o body estático; "" -> em branco.
  const applyTemplate = (e) => {
    const id = e.target.value;
    setTemplateId(id);
    const tpl = (DOC_TEMPLATES[form.documentType] || []).find((t) => t.id === id);
    if (!tpl) {
      setTplValues({});
      setForm((prev) => ({ ...prev, title: "", body: "" }));
      return;
    }
    if (tpl.fields && tpl.build) {
      const vals = initValues(tpl);
      setTplValues(vals);
      setForm((prev) => ({ ...prev, title: tpl.title || "", body: tpl.build(vals) }));
    } else {
      setTplValues({});
      setForm((prev) => ({ ...prev, title: tpl.title || "", body: tpl.body || "" }));
    }
  };

  // Muda um campo do modelo -> recompõe o corpo a partir do build().
  const setTplField = (key) => (e) => {
    const value = e.target.value;
    setTplValues((prev) => {
      const next = { ...prev, [key]: value };
      if (activeTpl && activeTpl.build) {
        const body = activeTpl.build(next);
        setForm((f) => ({ ...f, body }));
      }
      return next;
    });
  };

  // Puxa itens do receituário do médico pro corpo da receita (append numerado,
  // continuando a numeração existente). Sai do modo "modelo" (o build() do modelo
  // sobrescreveria o corpo) — tudo segue 100% editável.
  const addFromReceituario = (selectedItems) => {
    setPickerOpen(false);
    if (!selectedItems || selectedItems.length === 0) return;
    setTemplateId("");
    setTplValues({});
    setForm((prev) => {
      const cur = prev.body || "";
      // Item COM conteúdo (composição completa, estilo MedX) entra INTEIRO;
      // senão, só o nome (+ posologia). Blocos separados por linha em branco.
      const blocks = selectedItems.map((it) => {
        if (it.body && it.body.trim()) return it.body.trim();
        const pos = it.posology ? ` — ${it.posology}` : "";
        return `${it.displayName}${pos}`;
      });
      const joined = blocks.join("\n\n");
      const sep = cur ? (cur.endsWith("\n") ? "\n" : "\n\n") : "";
      return { ...prev, body: cur + sep + joined };
    });
  };

  const handleEmit = async () => {
    if (!form.body || !form.body.trim()) {
      toast.error("Escreva o conteúdo do documento.");
      return;
    }
    const prof = professionals.find((p) => String(p.id) === String(form.professionalId));
    setSubmitting(true);
    try {
      await emitDocument(patientId, {
        documentType: form.documentType,
        title: form.title.trim() || undefined,
        body: form.body,
        professionalId: prof ? prof.id : undefined,
        professionalName: prof ? prof.name : undefined
      });
      toast.success("Documento emitido.");
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(emitDocErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = async (doc) => {
    setBusyId(doc.id);
    try {
      const url = await getEmittedDocObjectUrl(doc.id);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(emitDocErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (doc) => {
    setBusyId(doc.id);
    try {
      const url = await getEmittedDocObjectUrl(doc.id);
      const label = DOC_TYPE_LABELS[doc.documentType] || "Documento";
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}-${doc.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(emitDocErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmDelete = async () => {
    const doc = deleteTarget;
    setDeleteTarget(null);
    if (!doc) return;
    setBusyId(doc.id);
    try {
      await deleteEmittedDoc(doc.id);
      toast.success("Documento removido.");
      await load();
    } catch (err) {
      toast.error(emitDocErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const metaOf = (doc) => {
    const prof = doc.metadata && doc.metadata.professionalName;
    return { prof, title: (doc.metadata && doc.metadata.title) || DOC_TYPE_LABELS[doc.documentType] || "Documento" };
  };

  return (
    <div>
      <div className={classes.headerRow}>
        <Typography variant="h6" className={classes.title}>
          <DescriptionIcon fontSize="small" /> Documentos emitidos ({docs.length})
        </Typography>
        <Button variant="contained" color="primary" startIcon={<NoteAddIcon />} onClick={openDialog}>
          Emitir documento
        </Button>
      </div>

      {loading ? (
        <CircularProgress size={24} />
      ) : docs.length === 0 ? (
        <Typography variant="body2" className={classes.empty}>
          Nenhum documento emitido. Clique em "Emitir documento" para gerar receita, atestado ou pedido de exame.
        </Typography>
      ) : (
        docs.map((doc) => {
          const info = metaOf(doc);
          return (
            <Paper key={doc.id} variant="outlined" className={classes.card}>
              <DescriptionIcon style={{ color: "#6d4c9f" }} />
              <div className={classes.info}>
                <div className={classes.docTitle}>{info.title}</div>
                <div className={classes.meta}>
                  <Chip
                    size="small"
                    label={DOC_TYPE_LABELS[doc.documentType] || "Documento"}
                    variant="outlined"
                    style={{ marginRight: 8 }}
                  />
                  {fmtDateTime(doc.issuedAt)}
                  {info.prof ? ` · ${info.prof}` : ""}
                </div>
              </div>
              <div className={classes.actions}>
                {busyId === doc.id ? (
                  <CircularProgress size={22} style={{ margin: 8 }} />
                ) : (
                  <>
                    <Tooltip title="Enviar ao paciente">
                      <IconButton size="small" onClick={() => setSendTarget(doc)}>
                        <SendIcon fontSize="small" style={{ color: "#25D366" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver">
                      <IconButton size="small" onClick={() => handleView(doc)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Baixar">
                      <IconButton size="small" onClick={() => handleDownload(doc)}>
                        <GetAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => setDeleteTarget(doc)}>
                        <DeleteOutlineIcon fontSize="small" style={{ color: "#d32f2f" }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>
            </Paper>
          );
        })
      )}

      <SendDocumentDialog
        open={!!sendTarget}
        doc={sendTarget}
        defaultPhone={patient && patient.phone}
        defaultEmail={patient && patient.email}
        onClose={() => setSendTarget(null)}
      />

      {/* Diálogo de emissão */}
      <Dialog open={dialogOpen} onClose={() => (!submitting ? setDialogOpen(false) : null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Emitir documento</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Tipo"
            value={form.documentType}
            onChange={setType}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
          >
            {DOC_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Modelo"
            value={templateId}
            onChange={applyTemplate}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            helperText="Escolha um modelo pronto ou deixe em branco pra escrever do zero"
          >
            <MenuItem value="">Em branco (personalizado)</MenuItem>
            {(DOC_TEMPLATES[form.documentType] || []).map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
            ))}
          </TextField>

          {/* Campos do modelo (preenchem o conteúdo sozinhos) */}
          {activeFields && (
            <div className={classes.fieldsBox}>
              {activeFields.map((f) => (
                <TextField
                  key={f.key}
                  label={f.label}
                  type={f.type === "multiline" ? "text" : f.type}
                  value={tplValues[f.key] || ""}
                  onChange={setTplField(f.key)}
                  variant="outlined"
                  size="small"
                  multiline={f.type === "multiline"}
                  minRows={f.type === "multiline" ? 3 : undefined}
                  InputLabelProps={f.type === "date" || f.type === "time" ? { shrink: true } : undefined}
                  style={{ flexGrow: 1, minWidth: f.type === "date" || f.type === "time" ? 150 : 130 }}
                />
              ))}
            </div>
          )}

          <TextField
            select
            label="Profissional"
            value={form.professionalId}
            onChange={setField("professionalId")}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            helperText="Aparece na assinatura. Se tiver certificado ICP-Brasil configurado, o documento é assinado digitalmente (ICP)."
          >
            <MenuItem value="">—</MenuItem>
            {professionals.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Título (opcional)"
            value={form.title}
            onChange={setField("title")}
            placeholder="ex: Uso contínuo, Afastamento, Pré-operatório..."
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            inputProps={{ maxLength: 200 }}
          />

          {form.documentType === "prescription" && (
            <div style={{ marginTop: 8, marginBottom: 2 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<LocalPharmacyIcon fontSize="small" />}
                onClick={() => setPickerOpen(true)}
              >
                Buscar no receituário
              </Button>
            </div>
          )}

          <TextField
            label={activeFields ? "Conteúdo (montado — ajuste se quiser)" : "Conteúdo"}
            value={form.body}
            onChange={setField("body")}
            placeholder={BODY_PLACEHOLDER[form.documentType]}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            multiline
            minRows={6}
            required
          />
          <DialogContentText style={{ fontSize: 12, marginTop: 8 }}>
            O documento é gerado em PDF com o cabeçalho da clínica. Se o profissional tiver certificado ICP-Brasil configurado, é assinado digitalmente (ICP-Brasil); senão, sai com o carimbo eletrônico verificável (QR).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleEmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <NoteAddIcon />}
          >
            {submitting ? "Gerando..." : "Emitir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar exclusão */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullScreen={isMobile}>
        <DialogTitle>Remover documento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover este documento emitido? A ação fica registrada na trilha de auditoria.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} style={{ color: "#d32f2f" }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <ReceituarioPicker
        open={pickerOpen}
        professionalId={form.professionalId}
        professionalName={selectedProf ? selectedProf.name : ""}
        onClose={() => setPickerOpen(false)}
        onConfirm={addFromReceituario}
      />
    </div>
  );
};

export default EmittedDocuments;
